import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { EmptyArray } from 'lib/array/arrayUtils'
import { useExploreItems, useExploreNearbyItems } from 'tripPlanner/hooks/api/explore'
import { useExploreOffers } from 'tripPlanner/hooks/api/exploreOffers'
import { useItineraryContext } from '../contexts/ItineraryContext'
import { computeTripDatesFromStops } from 'tripPlanner/utils/chatDates'
import { exploreCategoryQueryLabel, type ChatExploreCategory } from 'tripPlanner/config/exploreCategories'
import type { ExploreItem } from 'tripPlanner/types/common'
import type { ExploreOffer } from 'tripPlanner/api/explore/types'
import type { MapViewState } from 'tripPlanner/components/Chat/chatMapConstants'
import { areBoundsClose, getBoundsCenter, getMaxVisibleKm, isCoordinateWithinBounds, serialiseMapBounds, type MapBounds } from 'lib/maps/bounds'
import { buildStopExploreParams, type StopExploreSearch } from 'tripPlanner/utils/exploreStopParams'
import { buildExploreQuery, filterPlacesByReviewFloor, findDestinationNameInBounds, mergeExplorePlaces } from 'tripPlanner/utils/explorePlaces'
import { getUnattributedPlaceDescription } from 'tripPlanner/utils/placeDescription'
import { EXPLORE_NEARBY_LIMIT, EXPLORE_MAX_RADIUS_METRES } from 'tripPlanner/utils/explorePlacesConstants'
import {
  MARKER_TYPE_EXPLORE,
  MARKER_TYPE_EXPLORE_HOTEL,
  MARKER_TYPE_EXPLORE_EXPERIENCE,
  MARKER_SOURCE_EXPLORE,
  MARKER_SOURCE_EXPLORE_HOTEL,
  MARKER_SOURCE_EXPLORE_EXPERIENCE,
} from 'tripPlanner/utils/mapMarkerTransforms'
import type { MapMarkerComponent } from 'tripPlanner/utils/mapMarkerTransforms'

const BOUNDS_DEBOUNCE_MS = 400

// Past this, Google answers from the whole region rather than from what is on screen.
const MAX_SEARCHABLE_KM = 20
// Hysteresis, so crossing the threshold does not drop and refetch the markers.
const MAX_SEARCHABLE_KM_EXIT = 24

// Far wider than a search, because a chip still reads as somewhere to aim at across a continent.
// Past this the globe edge comes into frame, where the middle of the screen is no longer a place.
const MAX_CHIP_RANGE_KM = 2000
const MAX_CHIP_RANGE_KM_EXIT = 2400

/**
 * A listing search handed to Explore: Places reuse their query; hotels and experiences retain their loaded results
 * until an explicit area search replaces the handoff.
 */
export type ExploreHandoffSearch = StopExploreSearch & { listingResults?: Array<ExploreOffer> }

interface UseChatMapExploreFiltersArgs {
  mapViewState: MapViewState | undefined
  mapBounds: MapBounds | undefined
  isHotelFilterVisible: boolean
  isExperienceFilterVisible: boolean
}

export function useChatMapExploreFilters({
  mapViewState,
  mapBounds,
  isHotelFilterVisible,
  isExperienceFilterVisible,
}: UseChatMapExploreFiltersArgs) {
  const { itineraryData } = useItineraryContext()
  const tripDates = useMemo(
    () => computeTripDatesFromStops(itineraryData?.stops),
    [itineraryData?.stops],
  )
  const [selectedCategory, setSelectedCategory] = useState<ChatExploreCategory | undefined>(undefined)
  const [appliedBounds, setAppliedBounds] = useState<MapBounds | undefined>(undefined)
  const [pendingBounds, setPendingBounds] = useState<MapBounds | undefined>(undefined)
  const [handoffSearch, setHandoffSearch] = useState<ExploreHandoffSearch | undefined>(undefined)
  const [hasUserMovedSinceHandoff, setHasUserMovedSinceHandoff] = useState(false)

  const [isViewportSearchable, setIsViewportSearchable] = useState(false)
  const [isWithinChipRange, setIsWithinChipRange] = useState(false)

  useEffect(() => {
    if (!mapBounds) {
      setIsViewportSearchable(false)
      setIsWithinChipRange(false)
      return
    }
    const visibleKm = getMaxVisibleKm(mapBounds)
    setIsViewportSearchable(prev => (
      visibleKm <= (prev ? MAX_SEARCHABLE_KM_EXIT : MAX_SEARCHABLE_KM)
    ))
    setIsWithinChipRange(prev => (
      visibleKm <= (prev ? MAX_CHIP_RANGE_KM_EXIT : MAX_CHIP_RANGE_KM)
    ))
  }, [mapBounds])

  const isExploreFilterVisible = isWithinChipRange &&
    !isHotelFilterVisible &&
    !isExperienceFilterVisible

  // A handoff is the stop's list, not an area's, so the viewport gate does not apply to it.
  const isExploreFilterActive = isExploreFilterVisible &&
    Boolean(selectedCategory) &&
    (Boolean(handoffSearch) || isViewportSearchable)

  const activeSource = selectedCategory?.source
  const isGooglePlacesSource = activeSource === 'google-places'
  const isLeHotelsSource = activeSource === 'le-hotels'
  const isLeExperiencesSource = activeSource === 'le-experiences'
  const isLeOffersSource = isLeHotelsSource || isLeExperiencesSource

  // Bare, with no destination appended: a viewport this wide has no destination to name.
  const categoryLabel = useMemo(
    () => (selectedCategory ? exploreCategoryQueryLabel(selectedCategory.key) : undefined),
    [selectedCategory],
  )

  /** A point, not a zoom: at the wide end Mapbox's reported span and zoom disagree. */
  const exploreSearchTarget = useMemo(() => {
    if (isViewportSearchable || !mapViewState) return undefined

    return { lat: mapViewState.latitude, lng: mapViewState.longitude }
  }, [isViewportSearchable, mapViewState])

  const debouncedBoundsTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // Hotel or experience filter took priority → clear bounds but preserve category.
  // When the filter is dismissed the category will be restored and
  // fresh explore data fetched for the current viewport.
  useEffect(() => {
    if (isHotelFilterVisible || isExperienceFilterVisible) {
      setAppliedBounds(undefined)
      setPendingBounds(undefined)
    }
  }, [isHotelFilterVisible, isExperienceFilterVisible])

  useEffect(() => {
    if (!selectedCategory || !mapBounds) return undefined
    if (debouncedBoundsTimeoutRef.current) {
      clearTimeout(debouncedBoundsTimeoutRef.current)
    }
    debouncedBoundsTimeoutRef.current = setTimeout(() => {
      setPendingBounds(mapBounds)
    }, BOUNDS_DEBOUNCE_MS)
    return () => {
      if (debouncedBoundsTimeoutRef.current) {
        clearTimeout(debouncedBoundsTimeoutRef.current)
      }
    }
  }, [mapBounds, selectedCategory])

  // The map only reports on `moveend`, so the debounce agreeing with live bounds is what says it stopped.
  useEffect(() => {
    if (!isExploreFilterActive || appliedBounds || !pendingBounds || !mapBounds) return
    if (!areBoundsClose(pendingBounds, mapBounds)) return
    setAppliedBounds(pendingBounds)
  }, [appliedBounds, isExploreFilterActive, mapBounds, pendingBounds])

  const selectCategory = useCallback((
    category: ChatExploreCategory | undefined,
    handoff?: ExploreHandoffSearch,
  ) => {
    setSelectedCategory(category)
    setAppliedBounds(undefined)
    setPendingBounds(undefined)
    setHandoffSearch(handoff)
    setHasUserMovedSinceHandoff(false)
  }, [])

  /** A real gesture, not the camera move that arriving here performs. */
  const notifyUserMovedMap = useCallback(() => {
    setHasUserMovedSinceHandoff(true)
  }, [])

  // The applied area only: falling back to the live viewport re-keys the query on every pan.
  const fetchCenter = useMemo(
    () => (appliedBounds ? getBoundsCenter(appliedBounds) : undefined),
    [appliedBounds],
  )

  // --- Google Places path ---
  const queryBounds = appliedBounds

  // Naming the destination is what separates a useful result set from a weak one: a bare
  // "Top attractions" against a 10km bias returns a walking tour and no Opera House.
  const destinationInView = useMemo(
    () => findDestinationNameInBounds(itineraryData?.stops, queryBounds),
    [itineraryData?.stops, queryBounds],
  )

  // The drawer's params verbatim, so React Query serves what is already on screen.
  const handoffParams = useMemo(
    () => (handoffSearch && selectedCategory
      ? buildStopExploreParams(selectedCategory.key, handoffSearch)
      : undefined),
    [handoffSearch, selectedCategory],
  )

  // English, not the traveller's locale: Google is being asked a question, not shown one.
  const exploreQuery = useMemo(() => {
    if (!isExploreFilterActive || !selectedCategory || !isGooglePlacesSource || !categoryLabel) return undefined

    // The drawer's label, not the map's: a query differing by a word is a different cache entry.
    if (handoffParams) return handoffParams.explore.query

    return destinationInView
      ? buildExploreQuery(categoryLabel, destinationInView)
      : categoryLabel
  }, [categoryLabel, destinationInView, handoffParams, isExploreFilterActive, isGooglePlacesSource, selectedCategory])

  const restrictRadius = useMemo(() => {
    if (handoffParams) return handoffParams.explore.restrictRadius
    if (!queryBounds) return undefined
    const halfSpanMetres = Math.round((getMaxVisibleKm(queryBounds) / 2) * 1000)
    return Math.min(Math.max(halfSpanMetres, 1), EXPLORE_MAX_RADIUS_METRES)
  }, [handoffParams, queryBounds])

  const locationBias = useMemo(() => {
    if (handoffParams) return handoffParams.explore.locationBias
    if (!fetchCenter) return undefined
    return [fetchCenter.lat, fetchCenter.lng] as [number, number]
  }, [fetchCenter, handoffParams])

  const shouldFetchGooglePlaces = Boolean(exploreQuery && locationBias && isExploreFilterActive && isGooglePlacesSource)

  // Searching a new area re-keys the query; without this the pins blink out between keys.
  const {
    data: exploreItems = EmptyArray as Array<ExploreItem>,
    isInitialLoading: exploreItemsInitialLoading,
    isFetching: exploreItemsFetching,
    isPreviousData: exploreItemsArePrevious,
    isError: exploreItemsError,
    refetch: refetchExploreItems,
  } = useExploreItems(
    { query: exploreQuery, locationBias, restrictRadius },
    { enabled: shouldFetchGooglePlaces, keepPreviousData: true },
  )

  // Only the default category merges both sources: that is where the overlap was measured, and
  // every extra one would double this surface's Google traffic each time an area is searched.
  const isDefaultExploreCategory = selectedCategory?.key === 'top-attractions'
  const isNearbyLegActive = shouldFetchGooglePlaces && isDefaultExploreCategory

  const nearbyParams = useMemo(
    () => handoffParams?.nearby ?? { location: locationBias, radius: restrictRadius, limit: EXPLORE_NEARBY_LIMIT },
    [handoffParams, locationBias, restrictRadius],
  )

  const {
    data: nearbyItems = EmptyArray as Array<ExploreItem>,
    isInitialLoading: nearbyInitialLoading,
    isFetching: nearbyFetching,
    isPreviousData: nearbyItemsArePrevious,
    isError: nearbyItemsError,
    refetch: refetchNearbyItems,
  } = useExploreNearbyItems(nearbyParams, {
    enabled: isNearbyLegActive,
    keepPreviousData: true,
  })

  // --- LE Offers path (Hotels & Experiences) ---
  const leOfferType = isLeHotelsSource ? 'hotel' : isLeExperiencesSource ? 'experience' : undefined

  const boundsUrlValue = useMemo(() => {
    if (handoffSearch?.listingResults || !appliedBounds || !isLeOffersSource || !isExploreFilterActive) return undefined
    return serialiseMapBounds(appliedBounds)
  }, [appliedBounds, handoffSearch, isExploreFilterActive, isLeOffersSource])

  const {
    data: offersData,
    isInitialLoading: offersInitialLoading,
    isFetching: offersFetching,
    isError: offersError,
    refetch: refetchOffers,
  } = useExploreOffers(
    {
      bounds: boundsUrlValue,
      offerTypes: leOfferType,
      checkIn: tripDates?.startDate,
      checkOut: tripDates?.endDate,
    },
    { enabled: Boolean(boundsUrlValue) },
  )

  const exploreOffers = useMemo<Array<ExploreOffer>>(() => {
    if (!isExploreFilterActive || !isLeOffersSource) return EmptyArray as Array<ExploreOffer>
    return handoffSearch?.listingResults ?? offersData?.offers ?? EmptyArray as Array<ExploreOffer>
  }, [handoffSearch, isExploreFilterActive, isLeOffersSource, offersData])

  const boundsForDisplay = appliedBounds ?? mapBounds

  const qualifyingExploreItems = useMemo(
    () => filterPlacesByReviewFloor(
      isDefaultExploreCategory ? mergeExplorePlaces(nearbyItems, exploreItems) : exploreItems,
    ),
    [exploreItems, isDefaultExploreCategory, nearbyItems],
  )

  // A handoff shows the drawer's list whole; trimming it to the frame defeats the point.
  const visibleExploreItems = useMemo(() => {
    if (!isExploreFilterActive || !isGooglePlacesSource) {
      return EmptyArray as Array<ExploreItem>
    }
    if (handoffSearch) return qualifyingExploreItems
    if (!boundsForDisplay) return EmptyArray as Array<ExploreItem>
    return qualifyingExploreItems.filter((item) => isCoordinateWithinBounds(item.place.point, boundsForDisplay))
  }, [boundsForDisplay, handoffSearch, isExploreFilterActive, isGooglePlacesSource, qualifyingExploreItems])

  const googlePlacesMarkers = useMemo<Array<MapMarkerComponent>>(() => {
    if (!isExploreFilterActive || visibleExploreItems.length === 0 || !selectedCategory || !isGooglePlacesSource) {
      return EmptyArray as Array<MapMarkerComponent>
    }
    return visibleExploreItems.map((item) => ({
      lat: item.place.point.lat,
      lng: item.place.point.lng,
      label: item.name,
      description: getUnattributedPlaceDescription(item),
      placeId: item.id,
      rating: item.rating,
      userRatingCount: item.userRatingCount,
      photoName: item.photo?.name,
      markerId: item.id,
      markerType: MARKER_TYPE_EXPLORE,
      categoryKey: selectedCategory.key,
      source: MARKER_SOURCE_EXPLORE,
    }))
  }, [isExploreFilterActive, isGooglePlacesSource, selectedCategory, visibleExploreItems])

  const leHotelMarkers = useMemo<Array<MapMarkerComponent>>(() => {
    if (!isExploreFilterActive || !isLeHotelsSource || exploreOffers.length === 0 || !selectedCategory) {
      return EmptyArray as Array<MapMarkerComponent>
    }
    return exploreOffers.map((offer) => ({
      lat: offer.coordinates.lat,
      lng: offer.coordinates.lng,
      label: offer.name,
      markerId: offer.id,
      offerId: offer.id,
      imageId: offer.imageIds[0],
      offerPrice: offer.price,
      markerType: MARKER_TYPE_EXPLORE_HOTEL,
      categoryKey: selectedCategory.key,
      source: MARKER_SOURCE_EXPLORE_HOTEL,
    }))
  }, [exploreOffers, isExploreFilterActive, isLeHotelsSource, selectedCategory])

  const leExperienceMarkers = useMemo<Array<MapMarkerComponent>>(() => {
    if (!isExploreFilterActive || !isLeExperiencesSource || exploreOffers.length === 0 || !selectedCategory) {
      return EmptyArray as Array<MapMarkerComponent>
    }
    return exploreOffers.map((offer) => ({
      lat: offer.coordinates.lat,
      lng: offer.coordinates.lng,
      label: offer.name,
      markerId: offer.id,
      offerId: offer.id,
      imageId: offer.imageIds[0],
      offerPrice: offer.price,
      markerType: MARKER_TYPE_EXPLORE_EXPERIENCE,
      categoryKey: selectedCategory.key,
      source: MARKER_SOURCE_EXPLORE_EXPERIENCE,
    }))
  }, [exploreOffers, isExploreFilterActive, isLeExperiencesSource, selectedCategory])

  const exploreMarkers = isLeHotelsSource
    ? leHotelMarkers
    : isLeExperiencesSource
      ? leExperienceMarkers
      : googlePlacesMarkers

  // v4 `isLoading` means "no data for this key" and sticks on a disabled query, so derive
  // from `isInitialLoading` (skeleton) and `isFetching` (disable controls) instead. Kept data is
  // the previous search's, so the shelf reports loading while the pins stay put.
  const isShowingPreviousExplore = (shouldFetchGooglePlaces && exploreItemsArePrevious) ||
    (isNearbyLegActive && nearbyItemsArePrevious)
  const isExploreLoading = isLeOffersSource
    ? offersInitialLoading
    : exploreItemsInitialLoading || nearbyInitialLoading || isShowingPreviousExplore
  const isExploreFetching = isLeOffersSource
    ? offersFetching
    : exploreItemsFetching || nearbyFetching

  /*
   * The two Google legs are complementary, so one failing is a short list rather than no list --
   * the same rule the drawer applies. Only both failing is an error.
   */
  const isExploreError = isLeOffersSource
    ? Boolean(boundsUrlValue) && offersError
    : shouldFetchGooglePlaces && exploreItemsError && (!isNearbyLegActive || nearbyItemsError)

  const isExplorePartial = !isLeOffersSource &&
    !isExploreError &&
    ((shouldFetchGooglePlaces && exploreItemsError) || (isNearbyLegActive && nearbyItemsError))

  // Only the leg that failed, so a partial retry does not re-issue the one that answered.
  const refetchExplore = useCallback(() => {
    if (isLeOffersSource) {
      refetchOffers()
      return
    }
    const retryAll = !exploreItemsError && !nearbyItemsError
    if (exploreItemsError || retryAll) {
      refetchExploreItems()
    }
    if (isNearbyLegActive && (nearbyItemsError || retryAll)) {
      refetchNearbyItems()
    }
  }, [exploreItemsError, isLeOffersSource, isNearbyLegActive, nearbyItemsError, refetchExploreItems, refetchNearbyItems, refetchOffers])

  const isBoundsDirty = useMemo(() => {
    if (!isExploreFilterActive || !pendingBounds || !appliedBounds) {
      return false
    }
    // Arriving moves the camera twice, and neither is the traveller asking for a different area.
    if (handoffSearch && !hasUserMovedSinceHandoff) return false
    return !areBoundsClose(pendingBounds, appliedBounds)
  }, [appliedBounds, handoffSearch, hasUserMovedSinceHandoff, isExploreFilterActive, pendingBounds])

  const applyBounds = useCallback(() => {
    setHandoffSearch(undefined)
    // The caller answers this by moving the camera; the seeding effect pins where it lands.
    if (!isViewportSearchable) {
      setAppliedBounds(undefined)
      setPendingBounds(undefined)
      return
    }
    const nextBounds = mapBounds ?? pendingBounds
    if (!nextBounds) return
    setAppliedBounds(nextBounds)
    setPendingBounds(nextBounds)
  }, [isViewportSearchable, mapBounds, pendingBounds])

  // Clear all explore filter state (used on chat switch / new chat).
  // Cancels any pending debounce to prevent stale state after reset.
  const reset = useCallback(() => {
    setSelectedCategory(undefined)
    setAppliedBounds(undefined)
    setPendingBounds(undefined)
    setHandoffSearch(undefined)
    if (debouncedBoundsTimeoutRef.current) {
      clearTimeout(debouncedBoundsTimeoutRef.current)
      debouncedBoundsTimeoutRef.current = undefined
    }
  }, [])

  return {
    selectedCategory,
    selectCategory,
    exploreMarkers,
    exploreOffers,
    explorePlaces: visibleExploreItems,
    exploreHandoff: handoffSearch,
    exploreSearchTarget,
    isExploreFilterVisible,
    isExploreFilterActive,
    isExploreLoading,
    isExploreFetching,
    isExploreError,
    isExplorePartial,
    refetchExplore,
    isBoundsDirty,
    applyBounds,
    notifyUserMovedMap,
    reset,
  }
}
